import { useState } from "react";
import { IonSegment, IonSegmentButton, IonLabel, IonButton, IonIcon, IonContent, IonList, IonItem } from "@ionic/react";
import { caretDownOutline } from "ionicons/icons";
import FreshPopover from "./FreshPopover";

// The segment row of an admin table card (Purchases, Users, Saved Docs, the
// Subscriptions tables): each tab with its count. On desktop the scrollable
// segment; on a phone the current tab as the segment label (uppercase, its
// count beside it, a caret at its end) opening the 240px popover that lists
// the tabs in title case with their counts, a line after each but the last.
// `tabs` is [{ value, label }] (labels uppercase, as the segments show them),
// `counts` { value: n }.

// the segment's buttons: medium text, the text colour when checked, a flat
// underline indicator
const segBtnStyle = {
  "--color":           "var(--ion-color-medium)",
  "--color-checked":   "var(--ion-text-color)",
  "--indicator-color": "var(--ion-text-color)",
  "--border-radius":   "0",
  "--padding-top":     "0",
  "--padding-bottom":  "0",
  minHeight: 46,
  flexShrink: 0,
};

// the count beside a tab, one size everywhere
// The Filter button's count badge: pinned to one size (height, line height,
// font in px) so phones and desktops draw it identically, and told not to
// let a phone browser inflate it
export const filterCountBadgeStyle = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", marginInlineStart: 6,
  height: 14, minWidth: 14, padding: "0 5px", boxSizing: "border-box",
  background: "var(--ion-card-background)", borderRadius: 4,
  fontSize: 10.4, lineHeight: 1, fontWeight: 700, letterSpacing: 0, color: "var(--ion-color-medium)",
  WebkitTextSizeAdjust: "100%", textSizeAdjust: "100%",
};
export const countBadgeStyle = { display: "inline-flex", alignItems: "center", justifyContent: "center", height: 24, minWidth: 17, padding: "0 5px", boxSizing: "border-box", background: "var(--ion-background-color)", borderRadius: 4, fontSize: "0.65rem", fontWeight: 700, lineHeight: 1, letterSpacing: 0, color: "var(--ion-color-medium)" };

const titleCase = (s) => String(s).toLowerCase().replace(/(^|\s)\S/g, (m) => m.toUpperCase());

export default function CardTabs({ tabs = [], counts = {}, value, onChange, isMobile = false }) {
  const [menu, setMenu] = useState({ open: false, event: undefined });
  if (!tabs.length) return null;
  const count = (t) => counts[t.value] ?? 0;

  if (isMobile) {
    const current = tabs.find((t) => t.value === value) || tabs[0];
    return (
      <>
        <IonButton fill="clear" className="segment-button card-tabs-btn" onClick={(e) => setMenu({ open: true, event: e.nativeEvent })}
          style={{ "--color": "var(--ion-text-color)", margin: "4px 0 4px 8px" }}>
          {current.label}
          <span style={{ ...countBadgeStyle, marginLeft: 8 }}>{count(current)}</span>
          <IonIcon slot="end" icon={caretDownOutline} />
        </IonButton>
        <FreshPopover open={menu.open} event={menu.event} onClose={() => setMenu({ open: false, event: undefined })} side="bottom" alignment="start" style={{ "--width": "240px" }}>
          <IonContent>
            <IonList style={{ padding: "4px 0" }}>
              {tabs.map((t, i) => (
                <IonItem
                  key={t.value}
                  button
                  detail={false}
                  lines={i === tabs.length - 1 ? "none" : "inset"}
                  aria-current={t.value === value ? "page" : undefined}
                  onClick={() => { setMenu({ open: false, event: undefined }); if (t.value !== value) onChange?.(t.value); }}
                  style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--color": "var(--ion-text-color)", "--border-color": "var(--app-divider)" }}
                >
                  <IonLabel>{t.title || titleCase(t.label)}</IonLabel>
                  <span slot="end" style={countBadgeStyle}>{count(t)}</span>
                </IonItem>
              ))}
            </IonList>
          </IonContent>
        </FreshPopover>
        <div style={{ flex: "1 1 0%" }} />
      </>
    );
  }

  return (
    <IonSegment scrollable value={value} onIonChange={(e) => { if (e.detail.value !== value) onChange?.(e.detail.value); }} style={{ "--background": "transparent", flex: "1 1 0%" }}>
      {tabs.map((t) => (
        <IonSegmentButton key={t.value} value={t.value} layout="label-only" style={segBtnStyle}>
          <IonLabel style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
            {t.label}
            <span style={countBadgeStyle}>{count(t)}</span>
          </IonLabel>
        </IonSegmentButton>
      ))}
    </IonSegment>
  );
}
